/**
 * rpcDedupDiag - Diagnostic logging for RPC call deduplication
 * 
 * Tracks and logs critical path RPC calls per view to verify deduplication is working.
 * Only active in development mode with ?dedupDiag=1 URL parameter.
 */

// ============= Types =============
interface CallRecord {
  rpcName: string;
  timestamp: number;
  reason: string;
  deduped: boolean;
  cacheHit?: boolean;
}

interface ViewStats {
  viewId: string;
  startTime: number;
  calls: CallRecord[];
}

// ============= Global State =============
let currentView: ViewStats | null = null;
let enabled = false;

// Check URL param on module load
if (typeof window !== 'undefined') {
  const params = new URLSearchParams(window.location.search);
  enabled = import.meta.env.DEV && params.get('dedupDiag') === '1';
}

/**
 * Start tracking a new view (call on route change/mount)
 */
export function startViewTracking(viewId: string): void {
  if (!enabled) return;
  
  // Log previous view stats before starting new one
  if (currentView && currentView.calls.length > 0) {
    logViewSummary();
  }
  
  currentView = {
    viewId,
    startTime: Date.now(),
    calls: []
  };
  
  console.log(`[dedupDiag] 📊 Started tracking view: ${viewId}`);
}

/**
 * Record an RPC call
 */
export function recordRpcCall(
  rpcName: string, 
  reason: string, 
  deduped: boolean,
  cacheHit?: boolean
): void {
  if (!enabled || !currentView) return;
  
  currentView.calls.push({
    rpcName,
    timestamp: Date.now(),
    reason,
    deduped,
    cacheHit
  });
}

/**
 * Log summary of current view's RPC calls
 */
export function logViewSummary(): void {
  if (!enabled || !currentView) return;
  
  const { viewId, startTime, calls } = currentView;
  const duration = Date.now() - startTime;
  
  // Group calls by RPC name
  const grouped = calls.reduce((acc, call) => {
    if (!acc[call.rpcName]) {
      acc[call.rpcName] = { total: 0, deduped: 0, cacheHits: 0, fetched: 0 };
    }
    acc[call.rpcName].total++;
    if (call.deduped) acc[call.rpcName].deduped++;
    if (call.cacheHit) acc[call.rpcName].cacheHits++;
    if (!call.deduped && !call.cacheHit) acc[call.rpcName].fetched++;
    return acc;
  }, {} as Record<string, { total: number; deduped: number; cacheHits: number; fetched: number }>);
  
  console.group(`[dedupDiag] 📈 View Summary: ${viewId} (${duration}ms)`);
  console.table(grouped);
  
  // Check for violations (critical RPCs that should be 1x)
  const criticalRpcs = [
    'get_quiz_progress',
    'save_player_progress', 
    'save_quiz_progress',
    'set_player_session_token',
    'get_or_create_financial_status_with_session'
  ];
  
  const violations: string[] = [];
  for (const rpcName of criticalRpcs) {
    const stats = grouped[rpcName];
    if (stats && stats.fetched > 1) {
      violations.push(`⚠️ ${rpcName}: ${stats.fetched} actual fetches (should be ≤1)`);
    }
  }
  
  if (violations.length > 0) {
    console.warn('[dedupDiag] VIOLATIONS DETECTED:');
    violations.forEach(v => console.warn(v));
  } else {
    console.log('[dedupDiag] ✅ All critical RPCs within limits');
  }
  
  console.groupEnd();
}

/**
 * Force log current view summary (for debugging)
 */
export function forceLogSummary(): void {
  if (!currentView) {
    console.log('[dedupDiag] No view being tracked. Enable with ?dedupDiag=1');
    return;
  }
  logViewSummary();
}

/**
 * Check if diagnostic mode is enabled
 */
export function isDedupDiagEnabled(): boolean {
  return enabled;
}

/**
 * Enable/disable diagnostic mode
 */
export function setDedupDiagEnabled(value: boolean): void {
  enabled = value;
  console.log(`[dedupDiag] ${value ? 'ENABLED' : 'DISABLED'}`);
}

// Expose to window for debugging
if (typeof window !== 'undefined') {
  (window as any).__dedupDiag__ = {
    enable: () => setDedupDiagEnabled(true),
    disable: () => setDedupDiagEnabled(false),
    summary: forceLogSummary,
    isEnabled: isDedupDiagEnabled
  };
}
